import { ComponentType, ReactElement, isValidElement, useSyncExternalStore } from "react";
import type { MenuItem } from "../components/Sidenav";
import type { PluginSettings } from "../core-api-client";

export type PluginRoute = {
    /** Route path relative to the viewer root (e.g. "demo/items/:id"). Omit for index routes. */
    path?: string;
    /** Renders at the parent route's path. Only valid within `children`. */
    index?: boolean;
    element: ReactElement;
    children?: PluginRoute[];
};

export type PluginModule = {
    components?: Record<string, ComponentType<any>>;
    routes?: PluginRoute[];
    menuItems?: MenuItem[];
};

export type PluginDescriptor = PluginSettings;

type PluginRegistryEntry = PluginDescriptor & { module: PluginModule };

export type PluginRegistrySnapshot = {
    components: Record<string, ComponentType<any>>;
    routes: PluginRoute[];
    menuItems: MenuItem[];
};

const emptySnapshot: PluginRegistrySnapshot = { components: {}, routes: [], menuItems: [] };

let snapshot = emptySnapshot;
const listeners = new Set<() => void>();

export const getPluginSnapshot = () => snapshot;

export const subscribeToPlugins = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const usePluginComponents = () => useSyncExternalStore(subscribeToPlugins, () => getPluginSnapshot().components);
export const usePluginRoutes = () => useSyncExternalStore(subscribeToPlugins, () => getPluginSnapshot().routes);
export const getPluginMenuItems = () => getPluginSnapshot().menuItems;

// Top-level route segments owned by the viewer itself - a plugin route starting with one of these
// could out-rank the built-in routes (e.g. a static "storylines/custom" beats "storylines/:id").
export const RESERVED_TOP_LEVEL_SEGMENTS = ["storylines", "snapshots"];

export const validatePluginRoutes = (routes: PluginRoute[], pluginName: string): (PluginRoute & { path: string })[] => {
    const normalized = routes
        .map(route => ({ ...route, path: route.path?.replace(/^\/+/, "") }))
        .filter((route): route is PluginRoute & { path: string } => {
            if (!isValidElement(route.element)) {
                console.error(`Plugin '${pluginName}': route '${route.path ?? "(index)"}' has an invalid element and will be skipped.`);
                return false;
            }

            if (!route.path || route.index) {
                console.error(`Plugin '${pluginName}': top-level routes require a non-empty path (index routes are reserved for the home page). Skipping.`);
                return false;
            }

            const firstSegment = route.path.split("/")[0];
            if (firstSegment === "*" || RESERVED_TOP_LEVEL_SEGMENTS.includes(firstSegment)) {
                console.error(`Plugin '${pluginName}': route path '${route.path}' conflicts with a built-in route and will be skipped.`);
                return false;
            }

            return true;
        });

    return normalized;
};

const commitPlugins = (entries: PluginRegistryEntry[]) => {
    const next: PluginRegistrySnapshot = { components: {}, routes: [], menuItems: [] };
    const seenTopLevelPaths = new Set<string>();

    for (const entry of entries) {
        Object.assign(next.components, entry.module.components);

        for (const route of validatePluginRoutes(entry.module.routes ?? [], entry.name)) {
            if (seenTopLevelPaths.has(route.path)) {
                console.warn(`Plugin '${entry.name}': route path '${route.path}' is already registered by another plugin; the first registration wins on ranking ties.`);
            }
            seenTopLevelPaths.add(route.path);
            next.routes.push(route);
        }

        next.menuItems.push(...(entry.module.menuItems ?? []));
    }

    snapshot = next;
    listeners.forEach(listener => listener());
};

export const loadPluginModule = async (url: string, importer = (u: string) => import(/* @vite-ignore */ u)): Promise<PluginModule | null> => {
    try {
        const pluginModule: PluginModule = await importer(url);
        console.info(`Loaded plugin: ${url}`, pluginModule);
        return pluginModule;
    }
    catch (err) {
        console.error(`Failed to load plugin: ${url}`, err);
        return null;
    }
};

/**
 * Loads all tenant plugins and atomically swaps the registry, so a branch switch
 * never leaves stale components/routes behind or exposes a half-loaded registry.
 */
export const loadPlugins = async (plugins: PluginDescriptor[], importer?: (url: string) => Promise<any>) => {
    const resolved = applyPluginOverrides(plugins);

    syncPluginStylesheets(resolved);

    const entries = (await Promise.all(resolved.map(async plugin => ({
        ...plugin,
        module: await loadPluginModule(plugin.url, importer),
    })))).filter(entry => entry.module != null) as PluginRegistryEntry[];

    commitPlugins(entries);
};


// ---------------------------------------------------------------------------
// Stylesheets
// ---------------------------------------------------------------------------

/** Maps a plugin module URL to its sibling stylesheet URL (query string preserved), or null when the URL has no .js path. */
export const getPluginStylesheetUrl = (pluginUrl: string): string | null => {
    try {
        const url = new URL(pluginUrl);

        if (!/\.js$/i.test(url.pathname))
            return null;

        url.pathname = url.pathname.replace(/\.js$/i, ".css");
        return url.toString();
    }
    catch {
        return null;
    }
};

/** Replaces the previously injected plugin stylesheets with those of the given plugins (idempotent across branch switches). */
export const syncPluginStylesheets = (plugins: PluginDescriptor[]) => {
    document.head.querySelectorAll("link[data-plugin-stylesheet]").forEach(link => link.remove());

    plugins.forEach(plugin => {
        const stylesheetUrl = getPluginStylesheetUrl(plugin.url);
        if (!stylesheetUrl)
            return;

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = stylesheetUrl;
        link.crossOrigin = "anonymous";
        link.dataset.pluginStylesheet = plugin.name;
        document.head.appendChild(link);
    });
};


// ---------------------------------------------------------------------------
// Developer URL overrides
// ---------------------------------------------------------------------------

const PLUGIN_OVERRIDES_STORAGE_KEY = "qerent::plugin-overrides";

type PluginOverrideConfig = {
    /** Plugin name -> replacement module URL for plugins configured on the server. */
    overrides?: Record<string, string>;
    /** Plugins to load that are not configured on the server at all. */
    additionalPlugins?: PluginDescriptor[];
};

const readPluginOverrideConfig = (): PluginOverrideConfig => {
    const raw = localStorage.getItem(PLUGIN_OVERRIDES_STORAGE_KEY);
    if (!raw)
        return {};

    try {
        return JSON.parse(raw) ?? {};
    }
    catch (err) {
        console.warn(`Ignoring malformed plugin override config in localStorage ('${PLUGIN_OVERRIDES_STORAGE_KEY}'):`, err);
        return {};
    }
};

/** Applies the developer's localStorage URL overrides to the server-configured plugin list. */
export const applyPluginOverrides = (plugins: PluginDescriptor[]): PluginDescriptor[] => {
    const config = readPluginOverrideConfig();
    const overrides = config.overrides ?? {};

    const overriddenNames: string[] = [];
    const overridden = plugins.map(plugin => {
        if (!overrides[plugin.name]) return plugin;
        overriddenNames.push(plugin.name);
        return { ...plugin, url: overrides[plugin.name] };
    });
    const existingNames = new Set(plugins.map(plugin => plugin.name));
    const added = (config.additionalPlugins ?? []).filter(plugin => plugin.name && plugin.url && !existingNames.has(plugin.name));

    if (overriddenNames.length || added.length) {
        console.warn(
            "%c[Qerent] Plugin overrides are ACTIVE in this browser",
            "color:#fff;background:#c62828;padding:2px 6px;border-radius:2px",
            { overridden: overriddenNames, added: added.map(plugin => plugin.name) },
            "- run window.qerentPlugins.clear() and reload to remove them.",
        );
    }

    return [...overridden, ...added];
};

const writePluginOverrideConfig = (mutate: (config: PluginOverrideConfig) => void) => {
    const config = readPluginOverrideConfig();
    mutate(config);
    localStorage.setItem(PLUGIN_OVERRIDES_STORAGE_KEY, JSON.stringify(config));
    console.info("Plugin override config updated. Reload the page to apply.");
};

const pluginDevTools = {
    /** Replaces the URL of a server-configured plugin (this browser only). */
    override: (name: string, url: string) => writePluginOverrideConfig(config => {
        config.overrides = { ...config.overrides, [name]: url };
    }),
    /** Loads an extra plugin that is not configured on the server (this browser only). */
    add: (name: string, url: string) => writePluginOverrideConfig(config => {
        config.additionalPlugins = [...(config.additionalPlugins ?? []).filter(plugin => plugin.name !== name), { name, url }];
    }),
    /** Removes a single override/additional plugin by name. */
    remove: (name: string) => writePluginOverrideConfig(config => {
        delete config.overrides?.[name];
        config.additionalPlugins = config.additionalPlugins?.filter(plugin => plugin.name !== name);
    }),
    /** Removes all plugin overrides. */
    clear: () => {
        localStorage.removeItem(PLUGIN_OVERRIDES_STORAGE_KEY);
        console.info("Plugin overrides cleared. Reload the page to apply.");
    },
    /** Prints the currently configured overrides/additional plugins as a table. */
    list: () => {
        const { overrides = {}, additionalPlugins = [] } = readPluginOverrideConfig();
        console.table([
            ...Object.entries(overrides).map(([name, url]) => ({ type: "override", name, url })),
            ...additionalPlugins.map(plugin => ({ type: "additional", name: plugin.name, url: plugin.url })),
        ]);
    },
};

declare global {
    // eslint-disable-next-line no-var
    var qerentPlugins: typeof pluginDevTools;
}

if (typeof window !== "undefined") {
    window.qerentPlugins = pluginDevTools;
}
